/**
 * Static theme preset definitions.
 * CSS variables are applied when user selects a preset theme.
 *
 * NOTE: These are NOT stored in GlobalState - they are static.
 * User's ThemeEditor customizations remain separate and unaffected.
 */
export const THEME_PRESETS: Record<string, Record<string, string>> = {
  'blue-white': {
    // === V1: Canvas & Basis ===
    // Darker, more elegant blue palette
    '--streamlain-background-primary-color': '#102135',
    '--streamlain-background-secondary-color': '#0D2137',
    '--streamlain-background-tertiary-color': '#132F4C',
    '--streamlain-background-modal-color': 'rgba(10, 25, 41, 0.95)',
    '--streamlain-background-overlay-panel-color': '#0A1929',
    '--streamlain-edgeless-grid-color': 'rgba(255, 255, 255, 0.08)',

    '--streamlain-text-primary-color': '#FFFFFF',
    '--streamlain-text-secondary-color': '#B2BAC2',
    '--streamlain-text-disable-color': '#5A6A7A',

    '--streamlain-border-color': '#1E3A5F',
    '--streamlain-divider-color': '#132F4C',
    '--streamlain-icon-color': '#B2BAC2',
    '--streamlain-hover-color': '#132F4C',
    '--streamlain-hover-color-filled': 'rgba(255, 255, 255, 0.08)',
    '--streamlain-placeholder-color': '#5A6A7A',

    '--streamlain-brand-color': '#5090D3',
    '--streamlain-primary-color': '#5090D3',
    '--streamlain-secondary-color': '#7EB8F0',
    '--streamlain-tertiary-color': '#A8D4FF',

    '--streamlain-success-color': '#4CAF50',
    '--streamlain-warning-color': '#FF9800',
    '--streamlain-background-warning-color': 'rgba(255, 152, 0, 0.15)',
    '--streamlain-error-color': '#F44336',
    '--streamlain-background-error-color': 'rgba(244, 67, 54, 0.15)',

    // Dark theme overrides for opacity-based colors
    // (Light theme uses black with opacity, dark theme needs white with opacity)
    '--streamlain-black-30': 'rgba(255, 255, 255, 0.3)',

    // === V2: Layer Backgrounds ===
    '--streamlain-v2-layer-background-appChrome': '#0A1929',
    '--streamlain-v2-layer-background-primary': '#102135',
    '--streamlain-v2-layer-background-secondary': '#0D2137',
    '--streamlain-v2-layer-background-tertiary': '#132F4C',
    '--streamlain-v2-layer-background-overlayPanel': '#0D2137',
    '--streamlain-v2-layer-background-hoverOverlay': '#ffffff0d',
    '--streamlain-v2-layer-background-modal': '#000000cc',
    '--streamlain-v2-layer-background-codeBlock': '#0D2137',

    // === V2: Text ===
    '--streamlain-v2-text-primary': '#FFFFFF',
    '--streamlain-v2-text-secondary': '#B2BAC2',
    '--streamlain-v2-text-tertiary': '#8696A7',
    '--streamlain-v2-text-placeholder': '#5A6A7A',
    '--streamlain-v2-text-disable': '#3D4F61',
    '--streamlain-v2-text-emphasis': '#5090D3',
    '--streamlain-v2-text-link': '#7EB8F0',

    // === V2: Icons ===
    '--streamlain-v2-icon-primary': '#B2BAC2',
    '--streamlain-v2-icon-secondary': '#8696A7',
    '--streamlain-v2-icon-tertiary': '#5A6A7A',
    '--streamlain-v2-icon-activated': '#5090D3',
    '--streamlain-v2-icon-disable': '#3D4F61',

    // === V2: Borders ===
    '--streamlain-v2-layer-insideBorder-border': '#1E3A5F',
    '--streamlain-v2-layer-insideBorder-blackBorder': '#ffffff08',
    '--streamlain-v2-layer-insideBorder-whiteBorder': '#ffffff08',

    // === V2: Buttons ===
    '--streamlain-v2-button-primary': '#5090D3',
    '--streamlain-v2-button-secondary': '#ffffff12',
    '--streamlain-v2-button-buttonOverHover': '#132F4C',
    '--streamlain-v2-button-iconButtonSolid': '#ffffff12',
    '--streamlain-v2-button-disable': '#1E3A5F',
    '--streamlain-v2-button-pureWhiteText': '#ffffff',

    // === V2: Input ===
    '--streamlain-v2-input-background': '#ffffff0a',
    '--streamlain-v2-input-border-default': '#1E3A5F',
    '--streamlain-v2-input-border-active': '#5090D3',

    // === V2: Switch/Toggle ===
    '--streamlain-v2-switch-switchBackground-background': '#0D2137',
    '--streamlain-v2-switch-buttonBackground-active': '#132F4C',
    '--streamlain-v2-switch-buttonBackground-hover': '#ffffff0a',
    '--streamlain-v2-switch-fontColor-primary': '#FFFFFF',
    '--streamlain-v2-switch-fontColor-secondary': '#B2BAC2',
    '--streamlain-v2-switch-fontColor-tertiary': '#8696A7',
    '--streamlain-v2-switch-iconColor-active': '#FFFFFF',
    '--streamlain-v2-switch-iconColor-default': '#8696A7',

    // === V2: Tabs ===
    '--streamlain-v2-tab-tabBackground-default': '#0D2137',
    '--streamlain-v2-tab-tabBackground-active': '#132F4C',
    '--streamlain-v2-tab-tabBackground-hover': '#0D2137',
    '--streamlain-v2-tab-fontColor-active': '#FFFFFF',
    '--streamlain-v2-tab-fontColor-default': '#8696A7',
    '--streamlain-v2-tab-fontColor-hover': '#FFFFFF',
    '--streamlain-v2-tab-divider-divider': '#ffffff15',

    // === V2: Database/Table ===
    '--streamlain-v2-database-border': '#1E3A5F',
    '--streamlain-v2-database-buttonBackground': '#132F4C',
    '--streamlain-v2-database-textSecondary': '#8696A7',
    '--streamlain-v2-table-border': '#1E3A5F',
    '--streamlain-v2-table-headerBackground-default': '#0D2137',

    // === V2: Dialog/Tooltips ===
    '--streamlain-v2-dialog-background-primary': '#0D2137',
    '--streamlain-v2-tooltips-background': '#132F4C',
    '--streamlain-v2-tooltips-foreground': '#ffffff',
    '--streamlain-v2-slashMenu-background': '#0D2137',

    // === V2: Toast ===
    '--streamlain-v2-toast-cardLayer-second': '#0D2137',
    '--streamlain-v2-toast-cardLayer-third': '#132F4C',

    // === V2: AI Chat ===
    '--streamlain-v2-aI-userTextBackground': '#132F4C',

    // === V2: Comments ===
    // Using dark theme values for comment highlighting on dark background
    '--streamlain-v2-block-comment-hanelActive': '#ffffff0d',
    '--streamlain-v2-block-comment-highlightActive': '#1e96eb4d',
    '--streamlain-v2-block-comment-highlightDefault': '#1e96eb14',
    '--streamlain-v2-block-comment-highlightUnderline': '#1e96ebb2',

    // === V2: Avatar/Portrait ===
    '--streamlain-v2-portrait-localPortraitBackground': '#ffffff2b',

    // Note: edgeless/note/* variables are NOT overridden
    // so Notes stay white/light for readability
  },
};

/**
 * Check if a theme name is a preset theme.
 */
export const isPresetTheme = (theme: string | undefined): boolean => {
  if (!theme) return false;
  return theme in THEME_PRESETS;
};

/**
 * Get base color scheme for a preset.
 * All presets use 'light' colorScheme so that:
 * - Notes/Databases stay light and readable
 * - Native browser elements (scrollbars, inputs) stay light
 * - Canvas background is controlled by CSS variables
 */
export const getPresetColorScheme = (_theme: string): 'light' | 'dark' => {
  // Always return 'light' - canvas background is dark through CSS variables
  // but Notes/Databases must stay light for readability
  return 'light';
};
